import Phaser from 'phaser'
import { AudioManager } from '@/services/AudioManager'

export interface ButtonOpts {
  width?: number
  height?: number
  fontSize?: number
  color?: number
  pressColor?: number
  textColor?: string
  parent?: Phaser.GameObjects.Container
}

/**
 * 둥근 버튼. 입력은 고정 크기의 Zone이 받고(누름 시 시각만 축소되어 히트영역이 변하지 않음),
 * 시각은 별도 컨테이너로 처리한다. 좌표계가 물리 픽셀(DPR)이라 기본 크기/폰트는 화면폭 비례.
 */
export function makeButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  onClick: () => void,
  opts: ButtonOpts = {},
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const w = opts.width ?? W * 0.64
  const h = opts.height ?? W * 0.14
  const radius = h * 0.34
  const color = opts.color ?? 0xffb347
  const pressColor = opts.pressColor ?? 0xf59a2e
  const stroke = Math.max(2, W * 0.008)

  const g = scene.add.graphics()
  const draw = (fill: number) => {
    g.clear()
    g.fillStyle(0x000000, 0.18)
    g.fillRoundedRect(-w / 2, -h / 2 + h * 0.08, w, h, radius)
    g.fillStyle(fill, 1)
    g.fillRoundedRect(-w / 2, -h / 2, w, h, radius)
    g.lineStyle(stroke, 0xffffff, 1)
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, radius)
  }
  draw(color)

  const txt = scene.add
    .text(0, 0, label, {
      fontFamily: 'Arial',
      fontSize: `${Math.round(opts.fontSize ?? W * 0.052)}px`,
      color: opts.textColor ?? '#ffffff',
      fontStyle: 'bold',
      stroke: '#00000033',
      strokeThickness: Math.max(1, W * 0.004),
    })
    .setOrigin(0.5)

  const visual = scene.add.container(x, y, [g, txt])

  // 고정 히트 영역(Zone). 누름 시 visual만 축소하므로 가장자리 클릭도 안정적.
  const zone = scene.add.zone(x, y, w, h).setInteractive({ useHandCursor: true })
  if (opts.parent) opts.parent.add([visual, zone]) // 오버레이 등 컨테이너 소속으로
  const press = () => { draw(pressColor); visual.setScale(0.96) }
  const release = () => { draw(color); visual.setScale(1) }
  zone.on('pointerdown', press)
  zone.on('pointerup', () => { release(); scene.sound.unlock?.(); AudioManager.play('ui_click', 0.3); onClick() })
  zone.on('pointerout', release)
  zone.on('pointerupoutside', release)
  return zone
}

/**
 * 직접 그린 벡터 아이콘 원형 버튼(이모지보다 깔끔). drawIcon은 (0,0) 중심, 반지름 r 기준으로 그린다.
 */
export function makeGfxIconButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  drawIcon: (g: Phaser.GameObjects.Graphics, r: number) => void,
  onClick: () => void,
  r?: number,
  color = 0xffffff,
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const rad = r ?? W * 0.055
  const g = scene.add.graphics()
  g.fillStyle(0x000000, 0.18); g.fillCircle(0, rad * 0.12, rad)
  g.fillStyle(color, 1); g.fillCircle(0, 0, rad)
  g.lineStyle(Math.max(2, W * 0.006), 0xffffff, 1); g.strokeCircle(0, 0, rad)
  drawIcon(g, rad)
  const visual = scene.add.container(x, y, [g]).setDepth(40)
  const zone = scene.add.zone(x, y, rad * 2, rad * 2).setInteractive({ useHandCursor: true }).setDepth(41)
  zone.on('pointerdown', () => visual.setScale(0.9))
  zone.on('pointerup', () => { visual.setScale(1); scene.sound.unlock?.(); AudioManager.play('ui_click', 0.3); onClick() })
  zone.on('pointerout', () => visual.setScale(1))
  return zone
}

/** 일시정지 아이콘(둥근 막대 2개) */
export function drawPauseIcon(g: Phaser.GameObjects.Graphics, r: number) {
  const bw = r * 0.26, bh = r * 0.9, gap = r * 0.24
  g.fillStyle(0x5a4a8a, 1)
  g.fillRoundedRect(-gap - bw, -bh / 2, bw, bh, bw * 0.45)
  g.fillRoundedRect(gap, -bh / 2, bw, bh, bw * 0.45)
}

/** 나가기 아이콘(문 + 바깥으로 나가는 화살표) */
export function drawExitIcon(g: Phaser.GameObjects.Graphics, r: number) {
  const col = 0xc0392b
  // 문틀(왼쪽 ㄷ자)
  g.lineStyle(r * 0.16, col, 1)
  g.beginPath()
  g.moveTo(r * 0.05, -r * 0.62)
  g.lineTo(-r * 0.55, -r * 0.62)
  g.lineTo(-r * 0.55, r * 0.62)
  g.lineTo(r * 0.05, r * 0.62)
  g.strokePath()
  // 바깥으로 향하는 화살표
  g.lineStyle(r * 0.16, col, 1)
  g.beginPath()
  g.moveTo(-r * 0.2, 0)
  g.lineTo(r * 0.62, 0)
  g.strokePath()
  g.fillStyle(col, 1)
  g.beginPath()
  g.moveTo(r * 0.72, 0)
  g.lineTo(r * 0.4, -r * 0.28)
  g.lineTo(r * 0.4, r * 0.28)
  g.closePath()
  g.fillPath()
}

/** 원형 아이콘 버튼(일시정지/나가기 등). r은 반지름. */
export function makeIconButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  emoji: string,
  onClick: () => void,
  r?: number,
  color = 0xffffff,
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const rad = r ?? W * 0.05
  const g = scene.add.graphics()
  g.fillStyle(0x000000, 0.18); g.fillCircle(0, rad * 0.12, rad)
  g.fillStyle(color, 0.95); g.fillCircle(0, 0, rad)
  g.lineStyle(Math.max(2, W * 0.006), 0xffffff, 1); g.strokeCircle(0, 0, rad)
  const icon = scene.add.text(0, 0, emoji, { fontSize: `${Math.round(rad * 1.05)}px` }).setOrigin(0.5)
  const visual = scene.add.container(x, y, [g, icon]).setDepth(40)
  const zone = scene.add.zone(x, y, rad * 2, rad * 2).setInteractive({ useHandCursor: true }).setDepth(41)
  zone.on('pointerdown', () => visual.setScale(0.9))
  zone.on('pointerup', () => { visual.setScale(1); scene.sound.unlock?.(); AudioManager.play('ui_click', 0.3); onClick() })
  zone.on('pointerout', () => visual.setScale(1))
  return zone
}

/**
 * PNG 이미지 버튼(단일 상태). key=텍스처 키, size=반지름(표시 지름=2·size).
 * 누름 시 컨테이너만 축소하므로 히트영역(Zone)은 고정.
 */
export function makeImageButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  key: string,
  onClick: () => void,
  size?: number,
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const d = (size ?? W * 0.055) * 2
  const img = scene.add.image(0, 0, key).setDisplaySize(d, d)
  const visual = scene.add.container(x, y, [img]).setDepth(40)
  const zone = scene.add.zone(x, y, d, d).setInteractive({ useHandCursor: true }).setDepth(41)
  zone.on('pointerdown', () => visual.setScale(0.9))
  zone.on('pointerup', () => { visual.setScale(1); scene.sound.unlock?.(); AudioManager.play('ui_click', 0.3); onClick() })
  zone.on('pointerout', () => visual.setScale(1))
  zone.on('pointerupoutside', () => visual.setScale(1))
  return zone
}

/** 가로 PNG 버튼 + 라벨 텍스트 오버레이(좌측 아이콘 영역 피해 우측 중앙에 라벨). */
export function makeImageTextButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  key: string,
  label: string,
  onClick: () => void,
  opts: { width?: number; fontSize?: number; textColor?: string } = {},
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const img = scene.add.image(0, 0, key)
  const w = opts.width ?? W * 0.62
  const h = w * (img.height / img.width)
  img.setDisplaySize(w, h)
  const txt = scene.add
    .text(w * 0.05, -h * 0.04, label, {
      fontFamily: 'Arial', fontSize: `${Math.round(opts.fontSize ?? W * 0.05)}px`,
      color: opts.textColor ?? '#ffffff', fontStyle: 'bold',
      stroke: '#00000066', strokeThickness: Math.max(2, W * 0.006),
    })
    .setOrigin(0.5)
  const visual = scene.add.container(x, y, [img, txt])
  const zone = scene.add.zone(x, y, w, h * 0.78).setInteractive({ useHandCursor: true })
  zone.on('pointerdown', () => visual.setScale(0.96))
  zone.on('pointerup', () => { visual.setScale(1); scene.sound.unlock?.(); AudioManager.play('ui_click', 0.3); onClick() })
  zone.on('pointerout', () => visual.setScale(1))
  zone.on('pointerupoutside', () => visual.setScale(1))
  return zone
}

/** PNG 이미지 on/off 토글. 켜짐/꺼짐 텍스처 교체. size=반지름(표시 지름=2·size). */
export function makeImageToggle(
  scene: Phaser.Scene,
  x: number,
  y: number,
  onKey: string,
  offKey: string,
  initialOn: boolean,
  onToggle: (next: boolean) => void,
  size?: number,
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const d = (size ?? W * 0.06) * 2
  let on = initialOn
  const img = scene.add.image(0, 0, on ? onKey : offKey).setDisplaySize(d, d)
  const visual = scene.add.container(x, y, [img]).setDepth(40)
  const zone = scene.add.zone(x, y, d, d).setInteractive({ useHandCursor: true }).setDepth(41)
  zone.on('pointerdown', () => visual.setScale(0.9))
  zone.on('pointerup', () => {
    visual.setScale(1)
    on = !on
    img.setTexture(on ? onKey : offKey).setDisplaySize(d, d)
    scene.sound.unlock?.()
    onToggle(on)
  })
  zone.on('pointerout', () => visual.setScale(1))
  zone.on('pointerupoutside', () => visual.setScale(1))
  return zone
}

/**
 * 원형 오디오 on/off 토글. 켜짐=밝은 배경, 꺼짐=회색 배경 + 빨간 사선.
 * size는 반지름(물리 픽셀). 입력은 고정 Zone이 받는다.
 */
export function makeAudioToggle(
  scene: Phaser.Scene,
  x: number,
  y: number,
  emoji: string,
  initialOn: boolean,
  onToggle: (next: boolean) => void,
  size?: number,
): Phaser.GameObjects.Zone {
  const W = scene.scale.width
  const r = size ?? W * 0.05
  let on = initialOn
  const g = scene.add.graphics()
  const icon = scene.add
    .text(0, 0, emoji, { fontSize: `${Math.round(r * 1.05)}px` })
    .setOrigin(0.5)

  const draw = () => {
    g.clear()
    g.fillStyle(0x000000, 0.18)
    g.fillCircle(0, r * 0.12, r)
    g.fillStyle(on ? 0xffd166 : 0x9aa0ad, 1)
    g.fillCircle(0, 0, r)
    g.lineStyle(Math.max(2, W * 0.006), 0xffffff, 1)
    g.strokeCircle(0, 0, r)
    icon.setAlpha(on ? 1 : 0.55)
    if (!on) {
      g.lineStyle(Math.max(3, W * 0.011), 0xff3b3b, 0.95)
      g.beginPath()
      g.moveTo(-r * 0.6, -r * 0.6)
      g.lineTo(r * 0.6, r * 0.6)
      g.strokePath()
    }
  }
  draw()

  const visual = scene.add.container(x, y, [g, icon])
  const zone = scene.add.zone(x, y, r * 2, r * 2).setInteractive({ useHandCursor: true })
  zone.on('pointerdown', () => visual.setScale(0.9))
  zone.on('pointerup', () => {
    visual.setScale(1)
    on = !on
    draw()
    scene.sound.unlock?.()
    onToggle(on)
  })
  zone.on('pointerout', () => visual.setScale(1))
  zone.on('pointerupoutside', () => visual.setScale(1))
  return zone
}
